import { axiosInstance, AUTH_KEYS } from '../api';
import type { AxiosError } from 'axios';

const ANON_KEY = 'clicklaw.anonymousId';

/** Latest ingest outcome after each `trackEvent` (check in DevTools: `window.__CLICKLAW_ANALYTICS_LAST__`). */
export type AnalyticsIngestClientSummary = {
  at: string;
  event: string;
  endpoint: string;
  /** Whether ClickLaw successfully called Analytics `POST /api/events`. */
  ingest: 'skipped' | 'ok' | 'failed' | 'unknown';
  /** HTTP status from Analytics when `ingest` is `failed`; otherwise null. */
  analyticsHttpStatus: number | null;
  /** Snippet from Analytics or ClickLaw when failed/skipped; configuration hint when skipped. */
  detail: string | null;
  /** HTTP status from ClickLaw for the track request. */
  clicklawHttpStatus: number;
};

declare global {
  interface Window {
    __CLICKLAW_ANALYTICS_LAST__?: AnalyticsIngestClientSummary;
  }
}

let warnedSkippedThisPageLoad = false;

function applyIngestVisibility(
  clicklawResponse: { status: number; data?: unknown },
  context: { event: string; endpoint: string },
): void {
  if (typeof window === 'undefined') return;

  let ingest: AnalyticsIngestClientSummary['ingest'] = 'unknown';
  let analyticsHttpStatus: number | null = null;
  let detail: string | null = null;

  const body = clicklawResponse.data as
    | {
        data?: {
          analytics_ingest?: string;
          analytics_http_status?: number | null;
          analytics_detail?: string | null;
        };
      }
    | null
    | undefined;

  const raw = body?.data?.analytics_ingest;
  if (raw === 'ok' || raw === 'skipped' || raw === 'failed') {
    ingest = raw;
    analyticsHttpStatus =
      body?.data?.analytics_http_status === undefined || body?.data?.analytics_http_status === null
        ? null
        : Number(body.data.analytics_http_status);
    detail =
      body?.data?.analytics_detail === undefined || body?.data?.analytics_detail === null
        ? null
        : String(body.data.analytics_detail);
    if (Number.isNaN(analyticsHttpStatus!)) {
      analyticsHttpStatus = null;
    }
  }

  const row: AnalyticsIngestClientSummary = {
    at: new Date().toISOString(),
    event: context.event,
    endpoint: context.endpoint,
    ingest,
    analyticsHttpStatus,
    detail,
    clicklawHttpStatus: clicklawResponse.status,
  };

  window.__CLICKLAW_ANALYTICS_LAST__ = row;

  if (ingest === 'failed') {
    console.warn(
      '[ClickLaw → Analytics] Event reached ClickLaw but Analytics did not store it. Fix server-side (see analytics_http_status + detail on window.__CLICKLAW_ANALYTICS_LAST__).',
      row,
    );
  } else if (ingest === 'skipped' && !warnedSkippedThisPageLoad) {
    warnedSkippedThisPageLoad = true;
    console.warn(
      '[ClickLaw → Analytics] Ingest skipped: ClickLaw API has no analytics integration row (base_url / system_id / api_key).',
      row,
    );
  }
}

function safeGetReferrer(): string | null {
  try {
    return document.referrer || null;
  } catch {
    return null;
  }
}

function safeGetPagePath(): string | null {
  try {
    return window.location?.pathname || null;
  } catch {
    return null;
  }
}

function generateAnonymousId(): string {
  try {
    // Modern browsers
    const id = crypto.randomUUID();
    return id;
  } catch {
    // Fallback
    return `anon_${Date.now()}_${Math.random().toString(16).slice(2)}`;
  }
}

export function getAnonymousId(): string {
  try {
    const existing = localStorage.getItem(ANON_KEY);
    if (existing && existing.trim() !== '') return existing;
    const created = generateAnonymousId();
    localStorage.setItem(ANON_KEY, created);
    return created;
  } catch {
    return generateAnonymousId();
  }
}

export type AnalyticsMetadata = Record<string, unknown>;

export type AnalyticsUserSnapshot = {
  name?: string | null;
  email?: string | null;
  phone?: string | null;
};

export async function trackEvent(
  event: string,
  metadata?: AnalyticsMetadata,
  user?: AnalyticsUserSnapshot,
): Promise<void> {
  const trimmedEvent = event.trim();
  if (!trimmedEvent) return;

  const common: AnalyticsMetadata = {
    page_path: safeGetPagePath(),
    referrer: safeGetReferrer(),
  };

  const payload = {
    event: trimmedEvent,
    metadata: { ...common, ...(metadata ?? {}) },
  };

  const hasToken = Boolean(localStorage.getItem(AUTH_KEYS.accessToken));
  const endpoint = hasToken ? '/analytics/track' : '/analytics/track-public';

  try {
    if (endpoint === '/analytics/track-public') {
      const publicPayload = {
        ...payload,
        user: user ?? null,
      };

      const res = await axiosInstance.post(endpoint, publicPayload, {
        headers: {
          'X-Anonymous-Id': getAnonymousId(),
        },
      });
      applyIngestVisibility(res, { event: trimmedEvent, endpoint });
      return;
    }

    const res = await axiosInstance.post(endpoint, payload);
    applyIngestVisibility(res, { event: trimmedEvent, endpoint });
  } catch (unknownError) {
    const err = unknownError as AxiosError<unknown>;
    if (typeof window !== 'undefined') {
      const row: AnalyticsIngestClientSummary = {
        at: new Date().toISOString(),
        event: trimmedEvent,
        endpoint,
        ingest: 'unknown',
        analyticsHttpStatus: null,
        detail: err.message,
        clicklawHttpStatus: err.response?.status ?? 0,
      };
      window.__CLICKLAW_ANALYTICS_LAST__ = row;
      console.warn(
        '[ClickLaw analytics] Track request failed before ingest (network, 401, etc.). See window.__CLICKLAW_ANALYTICS_LAST__ and Network tab.',
        row,
        err.response?.data,
      );
    }
    // Never block UX on analytics failures
  }
}
